import apiClient from '../lib/apiClient';

// ─── Dashboard ──────────────────────────────────

export async function getDashboardSummary() {
  const { data } = await apiClient.get('/api/admin/dashboard/summary');
  return data as { totalUsers: number; monthlyRevenue: number; activeToday: number; aiRequestsToday: number };
}

export async function getUserGrowth(range: string = '30d') {
  const { data } = await apiClient.get(`/api/admin/dashboard/user-growth?range=${range}`);
  return data as Array<{ date: string; count: number }>;
}

export async function getPlanDistribution() {
  const { data } = await apiClient.get('/api/admin/dashboard/plan-distribution');
  return data as { free: number; pro: number; enterprise: number; conversionRate: number };
}

export async function getRecentActivity(limit: number = 10) {
  const { data } = await apiClient.get(`/api/admin/dashboard/recent-activity?limit=${limit}`);
  return data as Array<{ type: string; title: string; subtitle: string; timestamp: string }>;
}

// ─── Users ──────────────────────────────────────

export interface AdminUser {
  _id: string;
  email: string;
  displayName: string;
  plan: string;
  role: string;
  isActive: boolean;
  pastDue?: boolean;
  limitHit?: boolean;
  avatarUrl?: string;
  lastLoginAt?: string;
  createdAt: string;
  conversations: number;
  fileOps: number;
}

export interface AdminUserDetail {
  user: AdminUser & { emailVerified: boolean; stripeCustomerId?: string };
  subscription: {
    subscriptionId: string;
    plan: string;
    status: string;
    billingCycle: string;
    currentPeriodEnd: string;
    trialEnd?: string;
    cancelAtPeriodEnd: boolean;
    paymentMethod?: { type: string; brand?: string; last4?: string; expMonth?: number; expYear?: number };
  } | null;
  usage: {
    month: string; aiConversations: number; fileOperations: number; esignDocuments: number;
    retryCount: number; retryTokens: number; retryCostUsd: number;
    embeddingTokens: number; embeddingCostUsd: number; modelTokens: number; modelCostUsd: number;
  };
  integrations: Array<{ provider: string; status: string; lastSyncedAt?: string; lastError?: { message?: string } }>;
  recentConversations: Array<{ _id: string; title: string; messageCount: number; lastMessageAt: string; toolsUsed: string[] }>;
  recentPayments: Array<{ amount: number; currency: string; status: string; paidAt?: string; description: string }>;
}

export interface AdminUserUsageDetail {
  user: { _id: string; email: string; displayName: string };
  effectivePlan: { plan: string; source: string; hasActiveSubscription: boolean };
  currentMonthUsage: {
    month: string; aiConversations: number; fileOperations: number; esignDocuments: number;
    modelTokens: number; modelCostUsd: number; embeddingTokens: number; embeddingCostUsd: number;
    retryCount: number; retryTokens: number; retryCostUsd: number;
  };
  planLimits: { aiConversations: number | null; fileOperations: number | null; esignDocuments: number | null };
  recentRuns: Array<{
    runId: string; status: string; query: string; queryIntent?: string;
    startedAt: string; completedAt?: string; totalTokensUsed: number | null;
    estimatedCostUsd: number | null; userRetryCount: number; taskCount: number;
  }>;
  runSummary: { totalRuns: number; completedRuns: number; failedRuns: number; partialRuns: number; cancelledRuns: number };
}

export async function getUsers(params: { search?: string; plan?: string; status?: string; page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.plan) qs.set('plan', params.plan);
  if (params.status) qs.set('status', params.status);
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 20));
  const { data } = await apiClient.get(`/api/admin/users?${qs}`);
  return data as { users: AdminUser[]; total: number; page: number; totalPages: number };
}

export async function getUserDetail(userId: string) {
  const { data } = await apiClient.get(`/api/admin/users/${userId}`);
  return data as AdminUserDetail;
}

export async function updateUser(userId: string, updates: Record<string, unknown>) {
  const { data } = await apiClient.put(`/api/admin/users/${userId}`, updates);
  return data;
}

// ─── Revenue ────────────────────────────────────

export async function getRevenueSummary() {
  const { data } = await apiClient.get('/api/admin/revenue/summary');
  return data as { mrr: number; arr: number; churnRate: number; failedPayments: { count: number; amount: number } };
}

export async function getMrrTrend(range: string = '6m') {
  const { data } = await apiClient.get(`/api/admin/revenue/mrr-trend?range=${range}`);
  return data as Array<{ month: string; mrr: number }>;
}

export async function getSubscriptionBreakdown() {
  const { data } = await apiClient.get('/api/admin/revenue/subscriptions');
  return data as { proMonthly: number; proAnnual: number; enterprise: number };
}

export async function getPayments(page: number = 1) {
  const { data } = await apiClient.get(`/api/admin/revenue/payments?page=${page}&limit=10`);
  return data as { payments: Array<{ paymentId: string; userId: { _id: string; displayName: string; email: string }; amount: number; currency: string; status: string; paidAt?: string; invoiceUrl?: string; createdAt: string }>; total: number; page: number; totalPages: number };
}

// ─── Usage ──────────────────────────────────────

export async function getUsageSummary() {
  const { data } = await apiClient.get('/api/admin/usage/summary');
  return data as { month: string; aiConversations: number; fileOperations: number; esignDocuments: number; apiCost: number | null };
}

export async function getUsageByPlan() {
  const { data } = await apiClient.get('/api/admin/usage/by-plan');
  return data as Array<{ plan: string; avgAi: number; avgFiles: number; avgEsign: number; userCount: number; limits: { aiConversations: number | null; fileOperations: number | null; esignDocuments: number | null } }>;
}

export async function getLimitHits() {
  const { data } = await apiClient.get('/api/admin/usage/limit-hits');
  return data as { total: number; ai: number; files: number; esign: number; upgradeConversions?: number };
}

export async function getPopularTools() {
  const { data } = await apiClient.get('/api/admin/usage/popular-tools');
  return data as Array<{ tool: string; count: number }>;
}

export async function getWorkflowStats() {
  const { data } = await apiClient.get('/api/admin/usage/workflow-stats');
  return data as { total: number; completed: number; failed: number; avgDuration: number | null; uniqueWorkflows: number };
}

export async function getUserUsageDetail(userId: string) {
  const { data } = await apiClient.get(`/api/admin/usage/users/${userId}`);
  return data as AdminUserUsageDetail;
}

// ─── Documents ──────────────────────────────────

export async function getDocumentsSummary() {
  const { data } = await apiClient.get('/api/admin/documents/summary');
  return data as { totalConversations: number; importedDocs: number; sharedDocs: number; storageUsedMB: number };
}

export async function getDocuments(params: { search?: string; type?: string; page?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.type) qs.set('type', params.type);
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', '20');
  const { data } = await apiClient.get(`/api/admin/documents?${qs}`);
  return data as { documents: Array<{ _id: string; title: string; subtitle?: string; messageCount: number; lastMessageAt: string; toolsUsed: string[]; deletedAt?: string; userId: { _id: string; displayName: string; email: string } }>; total: number; page: number; totalPages: number };
}

// ─── Integrations ───────────────────────────────

export async function getIntegrationsSummary() {
  const { data } = await apiClient.get('/api/admin/integrations/summary');
  return data as Array<{ provider: string; connectedUsers: number; activeCount: number; errorCount: number; status: string }>;
}

export async function getWebhookEvents(page: number = 1) {
  const { data } = await apiClient.get(`/api/admin/integrations/webhook-events?page=${page}&limit=10`);
  return data as { events: Array<{ eventId: string; eventType: string; processed: boolean; error?: string; createdAt: string }>; total: number; page: number; totalPages: number };
}

// ─── E-Signatures ───────────────────────────────

export async function getEsignSummary() {
  const { data } = await apiClient.get('/api/admin/esign/summary');
  return data as { total: number; completed: number; pending: number; expired: number; revoked: number; completionRate: number };
}

export async function getEsignRequests(params: { status?: string; page?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', '20');
  const { data } = await apiClient.get(`/api/admin/esign/requests?${qs}`);
  return data as { requests: Array<{ requestId: string; documentName: string; status: string; signerProgress: { signed: number; total: number }; createdAt: string; completedAt?: string; expiresAt?: string; senderId: { _id: string; displayName: string; email: string }; pageCount?: number; fieldCount?: number }>; total: number; page: number; totalPages: number };
}

// ─── Settings ───────────────────────────────────

export async function getSettings() {
  const { data } = await apiClient.get('/api/admin/settings');
  return data as {
    general: { appName: string; clientUrl: string };
    planLimits: Record<string, { aiConversations: number; fileOperations: number; esignDocuments: number; filesPerConversation: number; maxFileSizeMB: number }>;
    environment: { mongodb: boolean; redis: boolean; stripe: boolean; postmark: boolean; storage: string; googleOAuth: boolean };
    auth: { accessTokenTtl: string; refreshTokenTtl: string };
  };
}

export async function purgeTrash() {
  const { data } = await apiClient.post('/api/admin/settings/purge-trash', {});
  return data as { ok: boolean; deleted: number };
}

export async function resetUsage() {
  const { data } = await apiClient.post('/api/admin/settings/reset-usage', {});
  return data as { ok: boolean; month: string };
}
